import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor, Smartphone, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";

const describe = (ua = "") => {
  const os = /iPhone|iPad/.test(ua) ? "iPhone / iPad" : /Android/.test(ua) ? "Android" : /Mac OS/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Unknown device";
  const br = /Edg\//.test(ua) ? "Edge" : /CriOS|Chrome\//.test(ua) ? "Chrome" : /FxiOS|Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  return { label: `${br} on ${os}`, mobile: /iPhone|iPad|Android|Mobile/.test(ua) };
};

export const DevicesList = () => {
  const { setUser } = useAuth();
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/auth/sessions").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const signOut = async (d) => {
    if (!window.confirm(d.current ? "Sign out this device? You'll need to sign in again." : `Sign out ${describe(d.user_agent).label}?`)) return;
    try {
      const { data } = await api.delete(`/auth/sessions/${d.id}`);
      if (data?.current) {
        setUser(false);
        return;
      }
      toast.success("Device signed out");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const signOutOthers = async () => {
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(`Signed out ${data?.signed_out ?? 0} other device(s)`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mt-12 max-w-2xl border-t border-white/10 pt-8" data-testid="admin-devices">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-display text-3xl uppercase text-white">Signed-in devices</p>
          <p className="text-sm text-slate-400">Anything you don't recognise? Sign it out and change your password.</p>
        </div>
        {items.length > 1 && (
          <button onClick={signOutOthers} data-testid="devices-signout-others" className="border border-[#ff3dc8] px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-white hover:bg-[#ff3dc8]">
            Sign out all others
          </button>
        )}
      </div>
      <div className="mt-5 space-y-3">
        {items.map((d) => {
          const info = describe(d.user_agent);
          const Icon = info.mobile ? Smartphone : Monitor;
          return (
            <div key={d.id} className="flex flex-wrap items-center gap-4 border border-white/10 bg-[#0f0f16] p-4" data-testid={`device-row-${d.id}`}>
              <Icon className="h-6 w-6 text-[#a259ff]" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white">{info.label} {d.current && <span className="ml-2 inline-block whitespace-nowrap bg-[#a259ff] px-2 py-0.5 align-middle font-mono text-[10px] uppercase tracking-widest text-black">This device</span>}</p>
                <p className="font-mono text-xs text-slate-400">IP {d.ip} · last active {new Date(d.last_seen).toLocaleString()} · first signed in {new Date(d.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => signOut(d)} data-testid={`device-signout-${d.id}`} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-[#ff7ad9]">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
